import firebase from "firebase/app";
import { convertToSchoolAiConfig, SchoolAiConfig } from "./school_ai_config";

export const schoolCollectionKey = "schools";
export const schoolConfigCollectionKey = "schoolConfigs";
export const schoolSecretCollectionKey = "schoolSecrets";
export const tenantPublicInfoCollectionKey = "tenantPublicInfo";

export const tenantThemePresets = [
  "blue",
  "sky",
  "teal",
  "green",
  "amber",
  "orange",
  "rose",
  "purple",
  "indigo",
  "slate"
] as const;
export type TenantThemePreset = (typeof tenantThemePresets)[number];
export const defaultTenantThemePreset: TenantThemePreset = "blue";

export function isTenantThemePreset(
  value: unknown
): value is TenantThemePreset {
  return tenantThemePresets.some(preset => preset === value);
}

export type TenantInfo = {
  isActive: boolean;
  slug: string;
  tenantName: string;
  themePreset?: TenantThemePreset;
  loginImagePath?: string;
  loginImageUrl?: string;
  /** ヘッダー・ログイン画面・帳票・ファビコンに出すテナントのロゴ */
  logoImagePath?: string;
  logoImageUrl?: string;
  /**
   * アイコン未設定のアプリに使う既定アイコン（通常・ホバー／AIの発言の2枚目）。
   * 未設定なら通常どおりマスコットを使う
   */
  defaultAppIconPath?: string;
  defaultAppIconUrl?: string;
  defaultAppIconAltPath?: string;
  defaultAppIconAltUrl?: string;
  /** ログイン画面の利用規約・プライバシーポリシー。未設定なら各リンクを出さない */
  termsOfUseUrl?: string;
  privacyPolicyUrl?: string;
  /**
   * ナビゲーション左下の「公式ガイド」「お問合せ」の遷移先（別タブ）。
   * テナントでは商品のガイド・チャット窓口を出さず、未設定なら項目ごと隠す
   */
  guideUrl?: string;
  contactUrl?: string;
};

export type TenantPublicInfo = {
  schoolDocId: string;
  isActive: boolean;
  tenantName: string;
  themePreset?: TenantThemePreset;
  loginImageUrl?: string;
  /** 未認証のログイン画面・ファビコンで使うロゴ */
  logoImageUrl?: string;
  termsOfUseUrl?: string;
  privacyPolicyUrl?: string;
};

export type SchoolAiUsageLimitPeriod =
  | "daily"
  | "weekly"
  | "monthly"
  | "yearly"
  | "all";

export type SchoolAiResourceLimitConfig = {
  enabled: boolean;
  period: SchoolAiUsageLimitPeriod;
  limit: number | null;
  warningThreshold?: number | null;
  countSystemPrompt?: boolean;
  expiresAt?: number | null;
};

export type SchoolAiUsageLimitScope = {
  chatMessage?: SchoolAiResourceLimitConfig;
  smartPrint?: SchoolAiResourceLimitConfig; // スマートプリント実行回数の上限（先生向け）
};

export type SchoolAiStudentUsageLimitScope = {
  chatMessage?: SchoolAiResourceLimitConfig;
  allowStudentIndividualChatMessageSettings?: boolean;
};

export type SchoolAiUsageLimits = {
  student?: SchoolAiStudentUsageLimitScope;
  tutor?: SchoolAiUsageLimitScope;
  schoolLevel?: SchoolAiUsageLimitScope;
};

export function getDefaultSchoolAiChatMessageLimitConfig(): SchoolAiResourceLimitConfig {
  return {
    enabled: false,
    period: "all",
    limit: null,
    warningThreshold: null,
    countSystemPrompt: false,
    expiresAt: null
  };
}

export function convertToSchoolAiResourceLimitConfig(
  data: firebase.firestore.DocumentData | undefined
): SchoolAiResourceLimitConfig | undefined {
  if (!data) {
    return undefined;
  }

  const defaultConfig = getDefaultSchoolAiChatMessageLimitConfig();
  const isValidPeriod = [
    "daily",
    "weekly",
    "monthly",
    "yearly",
    "all"
  ].includes(data.period);

  return {
    enabled: data.enabled ?? defaultConfig.enabled,
    period: isValidPeriod ? data.period : defaultConfig.period,
    limit:
      typeof data.limit === "number" || data.limit === null
        ? data.limit
        : defaultConfig.limit,
    warningThreshold:
      typeof data.warningThreshold === "number" ||
      data.warningThreshold === null
        ? data.warningThreshold
        : defaultConfig.warningThreshold,
    countSystemPrompt:
      data.countSystemPrompt ?? defaultConfig.countSystemPrompt,
    expiresAt:
      typeof data.expiresAt === "number" || data.expiresAt === null
        ? data.expiresAt
        : defaultConfig.expiresAt
  };
}

export function convertToSchoolAiUsageLimits(
  data: firebase.firestore.DocumentData | undefined
): SchoolAiUsageLimits | undefined {
  if (!data) {
    return undefined;
  }

  const convertToSchoolAiStudentUsageLimitScope = (
    scopeData: firebase.firestore.DocumentData | undefined,
    fallbackChatMessage?: SchoolAiResourceLimitConfig
  ): SchoolAiStudentUsageLimitScope | undefined => {
    if (!scopeData && !fallbackChatMessage) {
      return undefined;
    }

    const chatMessage =
      convertToSchoolAiResourceLimitConfig(scopeData?.chatMessage) ??
      fallbackChatMessage;
    const allowStudentIndividualChatMessageSettings =
      typeof scopeData?.allowStudentIndividualChatMessageSettings === "boolean"
        ? scopeData.allowStudentIndividualChatMessageSettings
        : true;

    if (
      !chatMessage &&
      typeof scopeData?.allowStudentIndividualChatMessageSettings !== "boolean"
    ) {
      return undefined;
    }

    return {
      ...(chatMessage ? { chatMessage } : {}),
      allowStudentIndividualChatMessageSettings
    };
  };

  const convertToSchoolAiUsageLimitScope = (
    scopeData: firebase.firestore.DocumentData | undefined
  ): SchoolAiUsageLimitScope | undefined => {
    const chatMessage = convertToSchoolAiResourceLimitConfig(
      scopeData?.chatMessage
    );
    const smartPrint = convertToSchoolAiResourceLimitConfig(
      scopeData?.smartPrint
    );

    if (!chatMessage && !smartPrint) {
      return undefined;
    }

    return {
      ...(chatMessage ? { chatMessage } : {}),
      ...(smartPrint ? { smartPrint } : {})
    };
  };

  const legacyChatMessage = convertToSchoolAiResourceLimitConfig(
    data.chatMessage
  );
  const student = convertToSchoolAiStudentUsageLimitScope(
    data.student,
    legacyChatMessage
  );
  // smartPrint のみ設定されたスコープでも legacy chatMessage を失わないようマージする
  const tutorScope = convertToSchoolAiUsageLimitScope(data.tutor);
  const tutor = tutorScope
    ? tutorScope.chatMessage || !legacyChatMessage
      ? tutorScope
      : { ...tutorScope, chatMessage: legacyChatMessage }
    : legacyChatMessage
    ? { chatMessage: legacyChatMessage }
    : undefined;
  const schoolLevel = convertToSchoolAiUsageLimitScope(data.schoolLevel);

  if (!student && !tutor && !schoolLevel) {
    return undefined;
  }

  return {
    ...(student ? { student } : {}),
    ...(tutor ? { tutor } : {}),
    ...(schoolLevel ? { schoolLevel } : {})
  };
}

export function convertToStudentSchoolAiUsageLimits(
  data: firebase.firestore.DocumentData | undefined
): SchoolAiUsageLimitScope | undefined {
  if (!data) {
    return undefined;
  }

  const chatMessage = convertToSchoolAiResourceLimitConfig(data.chatMessage);

  if (!chatMessage) {
    return undefined;
  }

  return {
    chatMessage
  };
}

export type School = {
  ref: firebase.firestore.DocumentReference;
  data: SchoolData;
};

export type SchoolData = {
  id: string; // 塾管理用ID (unique)
  name: string; // 塾名
  owner: firebase.firestore.DocumentReference | null; // 塾 owner スタッフのreference
  admins: string[]; // 塾に所属する owner | admin 権限を持つスタッフのID一覧 (security rule 用)
  supervisors?: string[]; // 塾に所属する supervisor 権限を持つスタッフのID一覧 (security rule 用)
  employees?: string[]; // 塾に所属する employee 権限を持つスタッフのID一覧 (security rule 用)
  tutors: string[]; // 塾に所属するスタッフのID一覧 (security rule 用)
  studentCount: number; // 塾に所属する生徒数
  classroomCount: number; // 塾に存在する教室数
  createdAt: number;
  suspended?: boolean; // アカウント停止状態
  labels?: firebase.firestore.DocumentReference[]; // 教室ごとの labels を定義
  allocatedStorageSize?: number; // Storage 使用量（MB）
  schoolAiConfig?: SchoolAiConfig;
  schoolAiUsageLimits?: SchoolAiUsageLimits;
  tenantInfo?: TenantInfo;
};

export type SchoolConfig = {
  ref: firebase.firestore.DocumentReference;
  data: SchoolConfigData;
};

export type Role = "serviceProvider" | null;

export type ParentSchool = {
  schoolDocRef: firebase.firestore.DocumentReference | string;
  schoolDocId: string;
  activateTime?: number; // 利用開始日時（unixtime）
  deactivateTime?: number; // 利用中止日時（unixtime）
};

export type ChildrenSchool = {
  schoolDocRef: firebase.firestore.DocumentReference | string;
  schoolDocId: string;
};

export interface SchoolDocumentReferences {
  schoolRef: firebase.firestore.DocumentReference;
  configRef: firebase.firestore.DocumentReference;
  secretRef: firebase.firestore.DocumentReference;
}

export const PREFECTURES = [
  "北海道",
  "青森県",
  "岩手県",
  "宮城県",
  "秋田県",
  "山形県",
  "福島県",
  "茨城県",
  "栃木県",
  "群馬県",
  "埼玉県",
  "千葉県",
  "東京都",
  "神奈川県",
  "新潟県",
  "富山県",
  "石川県",
  "福井県",
  "山梨県",
  "長野県",
  "岐阜県",
  "静岡県",
  "愛知県",
  "三重県",
  "滋賀県",
  "京都府",
  "大阪府",
  "兵庫県",
  "奈良県",
  "和歌山県",
  "鳥取県",
  "島根県",
  "岡山県",
  "広島県",
  "山口県",
  "徳島県",
  "香川県",
  "愛媛県",
  "高知県",
  "福岡県",
  "佐賀県",
  "長崎県",
  "熊本県",
  "大分県",
  "宮崎県",
  "鹿児島県",
  "沖縄県"
] as const;

export type Prefecture = (typeof PREFECTURES)[number];

export type SchoolCategory =
  | "elementary"
  | "junior_high"
  | "high"
  | "elementary_junior"
  | "junior_high_school"
  | "university"
  | "board_of_education"
  | "other";

/* eslint-disable @typescript-eslint/camelcase */
export const SCHOOL_CATEGORY_LABELS: Record<SchoolCategory, string> = {
  elementary: "小学校",
  junior_high: "中学校",
  high: "高等学校",
  elementary_junior: "小中一貫校",
  junior_high_school: "中高一貫校",
  university: "大学",
  board_of_education: "教育委員会",
  other: "その他"
};
/* eslint-enable @typescript-eslint/camelcase */

export type FoundingBody = "private" | "public" | "national" | "other";

export const FOUNDING_BODY_LABELS: Record<FoundingBody, string> = {
  private: "私立",
  public: "公立",
  national: "国立",
  other: "その他"
};

export type UserContractScope =
  | "tutor_and_student"
  | "tutor_only"
  | "student_only"
  | "other";

/* eslint-disable @typescript-eslint/camelcase */
export const USER_CONTRACT_SCOPE_LABELS: Record<UserContractScope, string> = {
  tutor_and_student: "先生+生徒",
  tutor_only: "先生のみ",
  student_only: "生徒のみ",
  other: "その他"
};
/* eslint-enable @typescript-eslint/camelcase */

export type SubscriptionType = "free" | "paid" | "other";

export const SUBSCRIPTION_TYPE_LABELS: Record<SubscriptionType, string> = {
  paid: "有料",
  free: "無料",
  other: "その他"
};

// ---- 塾向け機能（jukuFeaturesEnabled が有効なスクールに適用される定数群） ----

// 同時に有効（ホーム公開）にできるアプリ数の上限（スクールごとの設定が無いときのデフォルト）と、
// スーパー管理人がスクールごとに設定できる最大値
export const JUKU_MAX_ENABLED_PROMPTS = 10;
export const JUKU_MAX_ENABLED_PROMPTS_SETTABLE_MAX = 100;

// スマートプリント実行回数のデフォルト上限（1 日あたり）と設定可能な最大値
export const JUKU_SMART_PRINT_DEFAULT_LIMIT = 5;
export const JUKU_SMART_PRINT_MAX_LIMIT = 20;

export function isValidJukuMaxEnabledPrompts(value: unknown): value is number {
  return (
    typeof value === "number" &&
    Number.isInteger(value) &&
    value >= 1 &&
    value <= JUKU_MAX_ENABLED_PROMPTS_SETTABLE_MAX
  );
}

/**
 * スクールに適用する「同時に公開できるアプリ数」の上限を返す。
 * スーパー管理人がスクールごとに設定した値（jukuMaxEnabledPrompts）があればそれを、
 * 無い・不正な値のときはデフォルト（JUKU_MAX_ENABLED_PROMPTS）を使う。
 */
export function resolveJukuMaxEnabledPrompts(
  configData: Pick<SchoolConfigData, "jukuMaxEnabledPrompts"> | undefined
): number {
  const value = configData?.jukuMaxEnabledPrompts;
  return isValidJukuMaxEnabledPrompts(value) ? value : JUKU_MAX_ENABLED_PROMPTS;
}

// 塾向けプランのカタログ。プランを追加する場合はここに1行足す。
// 金額（月額・税込想定）はプラン表示用で、請求計算（functions の create_invoices）とは独立。
export type JukuPlanKey = "standard";

export type JukuPlan = {
  key: JukuPlanKey;
  name: string;
  monthlyFee: number; // 月額（円）
};

export const JUKU_PLANS: readonly JukuPlan[] = [
  { key: "standard", name: "Standard", monthlyFee: 9350 }
] as const;

export const DEFAULT_JUKU_PLAN_KEY: JukuPlanKey = "standard";

export function getJukuPlan(key: string | undefined): JukuPlan | undefined {
  return JUKU_PLANS.find(plan => plan.key === key);
}

/**
 * スクールに適用中の塾向けプランを返す。
 * 塾向け機能が有効なスクールは、明示設定が無ければ Standard 扱い。
 */
export function resolveJukuPlan(
  configData:
    | Pick<SchoolConfigData, "jukuFeaturesEnabled" | "jukuPlanKey">
    | undefined
): JukuPlan | undefined {
  if (!configData?.jukuFeaturesEnabled) {
    return getJukuPlan(configData?.jukuPlanKey);
  }
  return (
    getJukuPlan(configData.jukuPlanKey) ?? getJukuPlan(DEFAULT_JUKU_PLAN_KEY)
  );
}

// プラン変更履歴（append-only）。パス: schools/{schoolId}/jukuPlanChangeLogs/{autoId}
export const jukuPlanChangeLogCollectionKey = "jukuPlanChangeLogs";

export type JukuPlanChangeLog = {
  ref: firebase.firestore.DocumentReference;
  data: JukuPlanChangeLogData;
};

export type JukuPlanChangeLogData = {
  fromPlanKey: string | null; // 変更前プラン（初回設定は null）
  toPlanKey: string;
  changedAt: number; // unixtime
  changedBy: string; // 変更したスーパー管理人の uid
};

export function convertToJukuPlanChangeLog(
  data: firebase.firestore.DocumentData,
  ref: firebase.firestore.DocumentReference
): JukuPlanChangeLog {
  return {
    ref,
    data: {
      fromPlanKey:
        typeof data.fromPlanKey === "string" ? data.fromPlanKey : null,
      toPlanKey: data.toPlanKey,
      changedAt: data.changedAt,
      changedBy: data.changedBy
    }
  };
}

// school-ai のナビゲーション（グローバルナビ / 「もっと見る」メニュー）項目キー
type SchoolAiNavItemDefinition = {
  readonly key: string;
  readonly label: string;
  readonly note?: string;
};

function defineSchoolAiNavItems<T extends readonly SchoolAiNavItemDefinition[]>(
  items: T
): T {
  return items;
}

// 先生向けナビゲーションで非表示制御できる全項目。
// 通常表示・「もっと見る」・左下固定の表示順を基準に並べる。
export const SCHOOL_AI_TUTOR_NAV_ITEMS = defineSchoolAiNavItems([
  {
    key: "home",
    label: "ホーム",
    note: "ナビゲーションのみが非表示になります（ホーム画面は引き続き利用できます）"
  },
  { key: "manageModes", label: "アプリ管理" },
  { key: "submissionManagement", label: "提出管理" },
  { key: "notification", label: "通知メッセージ" },
  { key: "findApps", label: "アプリをみつける" },
  { key: "mypage", label: "マイページ" },
  {
    key: "moreMenu",
    label: "もっと見る",
    note: "非表示にすると「もっと見る」内の項目もナビゲーションから開けなくなります。ログアウトはPCでは直接表示され、スマホではマイページから行えます"
  },
  { key: "studentManagement", label: "生徒管理" },
  { key: "groupManagement", label: "グループ管理" },
  {
    key: "staffManagement",
    label: "先生管理",
    note: "オーナー・管理者・責任者の先生のみ表示されます"
  },
  { key: "classroomManagement", label: "教室管理" },
  {
    key: "ismManagement",
    label: "イズム管理",
    note: "テナントのオーナー・管理者の先生のみ表示されます"
  },
  { key: "journalManagement", label: "成長ジャーナル管理" },
  { key: "keywordFilter", label: "フィルター設定" },
  { key: "uiSetting", label: "UI設定" },
  { key: "logList", label: "ログ一覧" },
  { key: "submissionStatus", label: "提出状況" },
  { key: "chatData", label: "会話データ出力" },
  {
    key: "printSubmissionList",
    label: "プリント提出一覧",
    note: "プリント提出機能が有効な場合のみ表示されます"
  },
  {
    key: "printSubmission",
    label: "学習結果提出",
    note: "プリント提出機能が有効な場合のみ表示されます"
  },
  {
    key: "usageDashboard",
    label: "ダッシュボード",
    note: "オーナー・管理者権限の先生のみ表示されます"
  },
  {
    key: "usageLimit",
    label: "送信回数制限",
    note: "送信回数制限が有効なスクールのみ表示されます"
  },
  {
    key: "analysisReportList",
    label: "分析レポート一覧",
    note: "分析レポート表示が有効な場合のみ表示されます"
  },
  {
    key: "kawasemiLite",
    label: "教材作成（カワセミライト）",
    note: "カワセミライトが有効な場合のみ表示されます"
  },
  { key: "mingakuStudentManagement", label: "（旧）生徒管理画面へ" },
  {
    key: "manual",
    label: "公式ガイド",
    note: "スクール構成・設定によって表示されない場合があります"
  },
  {
    key: "contact",
    label: "お問合せ",
    note: "子スクールでは表示されません"
  }
] as const);

// 生徒向けナビゲーションで非表示制御できる全項目。
// 通常表示・「もっと見る」・左下固定の表示順を基準に並べる。
export const SCHOOL_AI_STUDENT_NAV_ITEMS = defineSchoolAiNavItems([
  {
    key: "home",
    label: "ホーム",
    note: "ナビゲーションのみが非表示になります（ホーム画面は引き続き利用できます）"
  },
  {
    key: "manageModes",
    label: "アプリ管理",
    note: "アプリ作成が許可された生徒のみ表示されます"
  },
  { key: "message", label: "メッセージ" },
  { key: "mypage", label: "マイページ" },
  {
    key: "moreMenu",
    label: "もっと見る",
    note: "非表示にすると「もっと見る」内の項目もナビゲーションから開けなくなります。ログアウトはPCでは直接表示され、スマホではマイページから行えます"
  },
  { key: "submissionStatus", label: "提出状況" },
  { key: "logList", label: "ログ一覧" },
  { key: "journals", label: "成長ジャーナル" },
  {
    key: "printSubmission",
    label: "学習結果提出",
    note: "プリント提出機能が有効な場合のみ表示されます"
  },
  { key: "manual", label: "生徒用ガイド" }
] as const);

// カタログからキーを導出し、表示管理への追加漏れを型エラーとして検出する。
export type SchoolAiTutorNavItemKey =
  (typeof SCHOOL_AI_TUTOR_NAV_ITEMS)[number]["key"];
export type SchoolAiStudentNavItemKey =
  (typeof SCHOOL_AI_STUDENT_NAV_ITEMS)[number]["key"];
export type SchoolAiNavItemKey =
  | SchoolAiTutorNavItemKey
  | SchoolAiStudentNavItemKey;

export type SchoolAiNavItemDef = {
  key: SchoolAiNavItemKey;
  label: string;
  note?: string; // 管理画面に表示する補足（他のフラグ等による表示条件）
};

// ロール別の非表示ナビゲーション項目（スーパー管理人が導入スクール管理画面で設定）
export type SchoolAiHiddenNavItems = {
  tutor?: SchoolAiTutorNavItemKey[];
  student?: SchoolAiStudentNavItemKey[];
};

/**
 * アプリ管理画面で非表示制御できるセクション（画面の表示順）。
 *
 * 新しい設定セクションを追加する場合、このカタログへ1件追加すると
 * SchoolAiAppManagementSectionKey と導入スクール管理画面の選択肢へ自動反映される。
 * ManagementScreen の全キー Record が未対応キーをビルド時に検出するので、
 * そのキーを追加し、表示領域には AppManagementAccordion を使用すること。
 */
type SchoolAiAppManagementSectionCatalogEntry = {
  readonly key: string;
  readonly label: string;
  readonly note?: string;
};

function defineSchoolAiAppManagementSectionCatalog<
  T extends readonly SchoolAiAppManagementSectionCatalogEntry[]
>(catalog: T): T {
  return catalog;
}

const SCHOOL_AI_APP_MANAGEMENT_SECTION_CATALOG =
  defineSchoolAiAppManagementSectionCatalog([
    {
      key: "basicInfo",
      label: "基本情報",
      note: "詳細画面の基本情報セクションが対象です（新規作成STEP1は表示されます）"
    },
    {
      key: "prompt",
      label: "プロンプト（AIへの指示文）",
      note: "新規アプリには標準プロンプトが自動設定され、既存の値は削除されません"
    },
    { key: "generativeAi", label: "生成AI設定" },
    { key: "voice", label: "音声設定" },
    { key: "accessControl", label: "アクセス制御" },
    { key: "preInputForm", label: "事前入力フォーム" },
    { key: "responseOptions", label: "返答選択肢" },
    { key: "keywordFilterActions", label: "キーワードフィルタ" },
    { key: "design", label: "デザイン" },
    {
      key: "digitalDrill",
      label: "デジタルドリルの設定",
      note: "デジタルドリルが有効なスクールのみ表示されます"
    },
    { key: "usageRestriction", label: "利用制限" },
    { key: "link", label: "リンク設定" },
    { key: "guide", label: "アプリ開始前のガイド表示設定" },
    {
      key: "pronunciationAssessment",
      label: "発音評価設定（試験運用版）"
    },
    {
      key: "aiCurriculum",
      label: "カリキュラム検討（試験運用中）",
      note: "学習カードが有効なスクールのみ表示されます"
    }
  ] as const);

// キーはカタログから導出し、キー定義と表示項目の二重管理を防ぐ。
export type SchoolAiAppManagementSectionKey =
  (typeof SCHOOL_AI_APP_MANAGEMENT_SECTION_CATALOG)[number]["key"];

export type SchoolAiAppManagementSectionDef = {
  key: SchoolAiAppManagementSectionKey;
  label: string;
  note?: string;
};

export const SCHOOL_AI_APP_MANAGEMENT_SECTIONS: readonly SchoolAiAppManagementSectionDef[] =
  SCHOOL_AI_APP_MANAGEMENT_SECTION_CATALOG;

export type SchoolConfigData = {
  paymentType: "uncontracted" | "stripe" | "external" | "none"; // 決済形態
  subscriptionType?: SubscriptionType; // 現在適用中の契約区分
  freeSubscriptionStartDate?: number; // 無料契約開始日(unixtime)
  freeSubscriptionEndDate?: number; // 無料契約終了日(unixtime)
  paidSubscriptionStartDate?: number; // 有料契約開始日(unixtime)
  paidSubscriptionEndDate?: number; // 有料契約終了日(unixtime)
  schoolAiUsageExpiresAt?: number | null; // スクールAI利用期限日(unixtime)
  customerEmail: string; // stripe customer email
  recessTime?: number; // 休会日時(unixtime)
  activateTime?: number; // 利用開始日時(unixtime)
  allowCreateRoom?: boolean; // 学習室作成権限
  allowUseOfCustomOfficialLine?: boolean; // カスタム公式LINEの利用許可
  lineOfficialAccountChannelAccessToken?: string; // LINE公式アカウトのチャネルアクセストークン
  lineOfficialAccountChannelSecret?: string; // LINE公式アカウトのチャネルシークレット
  liffId?: string; // LIFF ID
  target: firebase.firestore.DocumentReference;
  zoomUserId?: string;
  role?: Role[];
  useSchoolAiExclusively?: boolean;
  useStudyRoomOnly?: boolean; // 学習室のみ利用
  hideLoginInvitaion?: boolean;
  hideMingakuStudyRoom?: boolean;
  allowStudentsToChangePassword?: boolean;
  childrenSchools?: ChildrenSchool[];
  parentSchools?: ParentSchool[];
  childrenSchoolDocIds?: string[];
  parentSchoolDocIds?: string[];
  isPromptCreationForbidden?: boolean;
  disableStudentsPromptCreation?: boolean;
  canUseLearningCard?: boolean;
  canOwnerViewOtherStaffChats?: boolean;
  canUseDigitalDrill?: boolean;
  canUsePiP?: boolean; // Picture-in-Pictureの利用許可
  canShowKawasemiLight?: boolean; // 先生用メニューに教材作成（カワセミライト）を表示
  canUsePronunciationAssessment?: boolean;
  canShowTemplatesAndGuidesInSchoolAi?: boolean;
  canUsePrintSubmission?: boolean; // プリント提出機能の利用許可
  canPublishAppToStore?: boolean; // マーケットプレイスにアプリを公開できるか
  canSaveVoiceInputAudioInSchoolAiChat?: boolean; // スクールAIの音声入力ファイル保存を許可
  canShowAnalysisReport?: boolean; // 先生用「もっと見る」メニューに「分析レポート一覧」を表示する(CSレポート配布対象スクール)
  jukuFeaturesEnabled?: boolean; // 塾向け機能を有効にする（スーパー管理人が導入スクール管理画面で設定。領域が「塾」のスクールのみ対象）
  privateTutorModeEnabled?: boolean; // 家庭教師モード（スーパー管理人のみ設定可。無効時は家庭教師の生徒アクセスを停止）
  jukuPlanKey?: JukuPlanKey; // 塾向けプラン（未設定は Standard 扱い。スーパー管理人が設定）
  jukuMaxEnabledPrompts?: number; // 塾向け機能の「同時に公開できるアプリ数」の上限（未設定は JUKU_MAX_ENABLED_PROMPTS。スーパー管理人が設定）
  schoolAiHiddenNavItems?: SchoolAiHiddenNavItems; // school-aiのナビゲーションで非表示にする項目（スーパー管理人が導入スクール管理画面で設定）
  schoolAiHiddenAppManagementSections?: SchoolAiAppManagementSectionKey[]; // school-aiのアプリ管理画面で非表示にするセクション（スーパー管理人が導入スクール管理画面で設定）
  maxTutorCount?: number | null; // 先生アカウントの最大数（nullは無制限）
  maxStudentCount?: number | null; // 生徒アカウントの最大数（nullは無制限）
  contextCompressionCanaryEnabled?: boolean; // 会話圧縮カナリアリリース対象
  prefecture?: Prefecture;
  schoolCategory?: SchoolCategory;
  foundingBody?: FoundingBody;
  userContractScope?: UserContractScope;
};

export function isSchoolAiUsageExpired(
  expiresAt?: number | null,
  nowUnix: number = Math.floor(Date.now() / 1000)
): boolean {
  if (!expiresAt) {
    return false;
  }
  return nowUnix > expiresAt;
}

export type SchoolSecret = {
  ref: firebase.firestore.DocumentReference;
  data: SchoolSecretData;
};

export type SchoolSecretData = {
  customerId: string; // stripe customer id
  paymentMethods: string[]; // stripe attached payment method ids
};

export function convertToSchool(
  data: firebase.firestore.DocumentData,
  ref: firebase.firestore.DocumentReference
): School {
  const returnData: SchoolData = {
    id: data.id,
    name: data.name,
    owner: data.owner,
    admins: data.admins,
    supervisors: data.supervisors,
    employees: data.employees,
    tutors: data.tutors,
    labels: data.labels,
    studentCount: data.studentCount,
    classroomCount: data.classroomCount,
    createdAt: data.createdAt,
    schoolAiConfig: convertToSchoolAiConfig(data.schoolAiConfig)
  };

  if (data.suspended) {
    returnData.suspended = data.suspended;
  }
  if (data.allocatedStorageSize) {
    returnData.allocatedStorageSize = data.allocatedStorageSize;
  }
  if (data.schoolAiUsageLimits) {
    returnData.schoolAiUsageLimits = convertToSchoolAiUsageLimits(
      data.schoolAiUsageLimits
    );
  }
  if (data.tenantInfo) {
    const tenantInfo: TenantInfo = {
      isActive: data.tenantInfo.isActive,
      slug: data.tenantInfo.slug,
      tenantName: data.tenantInfo.tenantName
    };
    if (typeof data.tenantInfo.loginImagePath === "string") {
      tenantInfo.loginImagePath = data.tenantInfo.loginImagePath;
    }
    if (typeof data.tenantInfo.loginImageUrl === "string") {
      tenantInfo.loginImageUrl = data.tenantInfo.loginImageUrl;
    }
    if (typeof data.tenantInfo.logoImagePath === "string") {
      tenantInfo.logoImagePath = data.tenantInfo.logoImagePath;
    }
    if (typeof data.tenantInfo.logoImageUrl === "string") {
      tenantInfo.logoImageUrl = data.tenantInfo.logoImageUrl;
    }
    if (typeof data.tenantInfo.defaultAppIconPath === "string") {
      tenantInfo.defaultAppIconPath = data.tenantInfo.defaultAppIconPath;
    }
    if (typeof data.tenantInfo.defaultAppIconUrl === "string") {
      tenantInfo.defaultAppIconUrl = data.tenantInfo.defaultAppIconUrl;
    }
    if (typeof data.tenantInfo.defaultAppIconAltPath === "string") {
      tenantInfo.defaultAppIconAltPath = data.tenantInfo.defaultAppIconAltPath;
    }
    if (typeof data.tenantInfo.defaultAppIconAltUrl === "string") {
      tenantInfo.defaultAppIconAltUrl = data.tenantInfo.defaultAppIconAltUrl;
    }
    if (typeof data.tenantInfo.termsOfUseUrl === "string") {
      tenantInfo.termsOfUseUrl = data.tenantInfo.termsOfUseUrl;
    }
    if (typeof data.tenantInfo.privacyPolicyUrl === "string") {
      tenantInfo.privacyPolicyUrl = data.tenantInfo.privacyPolicyUrl;
    }
    if (typeof data.tenantInfo.guideUrl === "string") {
      tenantInfo.guideUrl = data.tenantInfo.guideUrl;
    }
    if (typeof data.tenantInfo.contactUrl === "string") {
      tenantInfo.contactUrl = data.tenantInfo.contactUrl;
    }
    if (isTenantThemePreset(data.tenantInfo.themePreset)) {
      tenantInfo.themePreset = data.tenantInfo.themePreset;
    }
    returnData.tenantInfo = tenantInfo;
  }
  return {
    ref,
    data: returnData
  };
}

export function convertToSchoolConfig(
  data: firebase.firestore.DocumentData,
  ref: firebase.firestore.DocumentReference
): SchoolConfig {
  const returnData: SchoolConfigData = {
    paymentType: data.paymentType,
    customerEmail: data.customerEmail,
    target: data.target
  };

  if (data.recessTime) {
    returnData.recessTime = data.recessTime;
  }

  if (data.activateTime) {
    returnData.activateTime = data.activateTime;
  }

  if (data.allowCreateRoom) {
    returnData.allowCreateRoom = data.allowCreateRoom;
  }

  if (data.allowUseOfCustomOfficialLine) {
    returnData.allowUseOfCustomOfficialLine = data.allowUseOfCustomOfficialLine;
  }

  if (data.lineOfficialAccountChannelAccessToken) {
    returnData.lineOfficialAccountChannelAccessToken =
      data.lineOfficialAccountChannelAccessToken;
  }

  if (data.lineOfficialAccountChannelSecret) {
    returnData.lineOfficialAccountChannelSecret =
      data.lineOfficialAccountChannelSecret;
  }

  if (data.liffId) {
    returnData.liffId = data.liffId;
  }

  if (data.zoomUserId) {
    returnData.zoomUserId = data.zoomUserId;
  }

  if (data.role) {
    returnData.role = data.role;
  }

  if (data.parentSchools) {
    returnData.parentSchools = data.parentSchools;
  }

  if (data.parentSchoolDocIds) {
    returnData.parentSchoolDocIds = data.parentSchoolDocIds;
  }

  if (data.childrenSchools) {
    returnData.childrenSchools = data.childrenSchools;
  }

  if (data.childrenSchoolDocIds) {
    returnData.childrenSchoolDocIds = data.childrenSchoolDocIds;
  }

  if (typeof data.useSchoolAiExclusively === "boolean") {
    returnData.useSchoolAiExclusively = data.useSchoolAiExclusively;
  } else {
    returnData.useSchoolAiExclusively = false;
  }

  if (typeof data.useStudyRoomOnly === "boolean") {
    returnData.useStudyRoomOnly = data.useStudyRoomOnly;
  } else {
    returnData.useStudyRoomOnly = false;
  }

  returnData.hideLoginInvitaion = data.hideLoginInvitaion ?? false;
  returnData.hideMingakuStudyRoom = data.hideMingakuStudyRoom ?? false;
  returnData.allowStudentsToChangePassword =
    data.allowStudentsToChangePassword ?? false;
  returnData.isPromptCreationForbidden =
    data.isPromptCreationForbidden ?? false;

  if (data.disableStudentsPromptCreation) {
    returnData.disableStudentsPromptCreation =
      data.disableStudentsPromptCreation;
  }

  if (data.canUseLearningCard) {
    returnData.canUseLearningCard = data.canUseLearningCard;
  }

  if (data.canOwnerViewOtherStaffChats) {
    returnData.canOwnerViewOtherStaffChats = data.canOwnerViewOtherStaffChats;
  }

  if (data.canUseDigitalDrill) {
    returnData.canUseDigitalDrill = data.canUseDigitalDrill;
  }

  if (data.canUsePiP) {
    returnData.canUsePiP = data.canUsePiP;
  }

  if (data.canShowKawasemiLight) {
    returnData.canShowKawasemiLight = data.canShowKawasemiLight;
  }

  if (data.canUsePronunciationAssessment) {
    returnData.canUsePronunciationAssessment =
      data.canUsePronunciationAssessment;
  }

  if (data.canShowTemplatesAndGuidesInSchoolAi) {
    returnData.canShowTemplatesAndGuidesInSchoolAi =
      data.canShowTemplatesAndGuidesInSchoolAi;
  }

  if (data.canUsePrintSubmission) {
    returnData.canUsePrintSubmission = data.canUsePrintSubmission;
  }

  if (data.canShowKawasemiLight) {
    returnData.canShowKawasemiLight = data.canShowKawasemiLight;
  }

  if (data.maxTutorCount !== null && data.maxTutorCount !== undefined) {
    returnData.maxTutorCount = data.maxTutorCount;
  }

  if (data.maxStudentCount !== null && data.maxStudentCount !== undefined) {
    returnData.maxStudentCount = data.maxStudentCount;
  }

  if (data.canPublishAppToStore) {
    returnData.canPublishAppToStore = data.canPublishAppToStore;
  }

  if (typeof data.canSaveVoiceInputAudioInSchoolAiChat === "boolean") {
    returnData.canSaveVoiceInputAudioInSchoolAiChat =
      data.canSaveVoiceInputAudioInSchoolAiChat;
  }

  if (typeof data.canShowAnalysisReport === "boolean") {
    returnData.canShowAnalysisReport = data.canShowAnalysisReport;
  }

  if (typeof data.jukuFeaturesEnabled === "boolean") {
    returnData.jukuFeaturesEnabled = data.jukuFeaturesEnabled;
  }
  returnData.privateTutorModeEnabled = data.privateTutorModeEnabled === true;

  if (getJukuPlan(data.jukuPlanKey)) {
    returnData.jukuPlanKey = data.jukuPlanKey;
  }

  if (isValidJukuMaxEnabledPrompts(data.jukuMaxEnabledPrompts)) {
    returnData.jukuMaxEnabledPrompts = data.jukuMaxEnabledPrompts;
  }

  if (data.schoolAiHiddenNavItems) {
    returnData.schoolAiHiddenNavItems = data.schoolAiHiddenNavItems;
  }

  if (data.schoolAiHiddenAppManagementSections) {
    returnData.schoolAiHiddenAppManagementSections =
      data.schoolAiHiddenAppManagementSections;
  }

  if (data.contextCompressionCanaryEnabled) {
    returnData.contextCompressionCanaryEnabled =
      data.contextCompressionCanaryEnabled;
  }

  if (
    data.subscriptionType &&
    data.subscriptionType in SUBSCRIPTION_TYPE_LABELS
  ) {
    returnData.subscriptionType = data.subscriptionType;
  }

  if (data.prefecture && PREFECTURES.includes(data.prefecture)) {
    returnData.prefecture = data.prefecture;
  }

  if (data.schoolCategory && data.schoolCategory in SCHOOL_CATEGORY_LABELS) {
    returnData.schoolCategory = data.schoolCategory;
  }

  if (data.foundingBody && data.foundingBody in FOUNDING_BODY_LABELS) {
    returnData.foundingBody = data.foundingBody;
  }

  if (
    data.userContractScope &&
    data.userContractScope in USER_CONTRACT_SCOPE_LABELS
  ) {
    returnData.userContractScope = data.userContractScope;
  }

  if (data.freeSubscriptionStartDate) {
    returnData.freeSubscriptionStartDate = data.freeSubscriptionStartDate;
  }

  if (data.freeSubscriptionEndDate) {
    returnData.freeSubscriptionEndDate = data.freeSubscriptionEndDate;
  }

  if (data.paidSubscriptionStartDate) {
    returnData.paidSubscriptionStartDate = data.paidSubscriptionStartDate;
  }

  if (data.paidSubscriptionEndDate) {
    returnData.paidSubscriptionEndDate = data.paidSubscriptionEndDate;
  }

  if (
    typeof data.schoolAiUsageExpiresAt === "number" ||
    data.schoolAiUsageExpiresAt === null
  ) {
    returnData.schoolAiUsageExpiresAt = data.schoolAiUsageExpiresAt;
  }

  return {
    ref,
    data: returnData
  };
}

export function convertToSchoolSecret(
  data: firebase.firestore.DocumentData,
  ref: firebase.firestore.DocumentReference
): SchoolSecret {
  const returnData: SchoolSecretData = {
    customerId: data.customerId,
    paymentMethods: data.paymentMethods
  };

  return {
    ref,
    data: returnData
  };
}
